import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import {
  getDiaActivo,
  abrirDia as apiAbrirDia,
  cerrarDia as apiCerrarDia,
} from '../services';
import { useAuth } from './AuthContext';

const DiaContext = createContext(null);

export function DiaProvider({ children }) {
  const { user, negocio } = useAuth();
  const [estado, setEstado] = useState({
    diaAbierto: false,
    abiertaAt:  null,
    diaId:      null,
    loading:    true,
  });

  // Carga el estado del día desde Supabase cuando hay sesión + negocio
  useEffect(() => {
    let activo = true;
    if (!user || !negocio) {
      setEstado({ diaAbierto: false, abiertaAt: null, diaId: null, loading: false });
      return undefined;
    }
    getDiaActivo()
      .then((d) => { if (activo) setEstado({ ...d, loading: false }); })
      .catch(() => { if (activo) setEstado({ diaAbierto: false, abiertaAt: null, diaId: null, loading: false }); });
    return () => { activo = false; };
  }, [user, negocio]);

  // Abre el día (compartido por todos los dispositivos del negocio).
  // `base` = efectivo con el que arranca la caja (se suma al arqueo).
  const abrirDia = useCallback(async (base = 0) => {
    try {
      const d = await apiAbrirDia(base);
      setEstado({ ...d, loading: false });
      return d;
    } catch {
      // Lo que de verdad impide dos días abiertos es el índice parcial
      // `idx_business_days_one_open` (supabase/migrations/0002_business_days.sql):
      // la consulta previa de `api.abrirDia` es comodidad del cliente, y entre
      // esa lectura y el INSERT cabe perfectamente otro dispositivo del
      // negocio. Cuando el índice dispara, el día SÍ quedó abierto —desde la
      // otra caja— y decirle al dueño "no pudimos abrir el día" sería mentirle.
      // Se vuelve a leer y, si ya está abierto, se trata como éxito.
      try {
        const activo = await getDiaActivo();
        if (activo?.diaAbierto) {
          setEstado({ ...activo, loading: false });
          return { ...activo, yaEstaba: true };
        }
      } catch { /* si tampoco se puede leer, cae al error de siempre */ }
      return null;
    }
  }, []);

  // Cierra el día; si se pasan datosArqueo se guardan en el histórico
  const cerrarDia = useCallback(async (datosArqueo = null) => {
    try {
      const d = await apiCerrarDia(datosArqueo);
      // `null` = no había día abierto (o el servidor no confirmó). No se toca
      // el estado ni se guarda nada: devolver null hace que Arqueo muestre el
      // error y CONSERVE el conteo del cajero en vez de darlo por guardado.
      if (!d) return null;
      setEstado({ ...d, loading: false });
      return d;
    } catch {
      return null;
    }
  }, []);

  return (
    <DiaContext.Provider value={{ ...estado, abrirDia, cerrarDia }}>
      {children}
    </DiaContext.Provider>
  );
}

export function useDia() {
  const ctx = useContext(DiaContext);
  if (!ctx) throw new Error('useDia debe usarse dentro de DiaProvider');
  return ctx;
}
